import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { cn } from '@muzly/lib/utils';
import { LANGUAGES } from '@muzly/shared/i18n';
import { Check, ChevronDown, ChevronLeft } from 'lucide-react';

import { queueProfileSync } from '@muzly/api-client';
import { useGoBack } from '@muzly/shared';

import { PROFILE_KEYS, loadProfile, saveProfile } from './profile';
import { ProfilePhotoField } from './ProfilePhotoField';
import { toProfileBody } from './toProfileBody';
import { BOTTOM_INSET } from '../../layouts';
import {
  SURVEY_STEPS,
  optionLabelParams,
  surveyStepById,
} from '../onboarding/survey';

import type { ProfileKey } from './profile';
import type { OnboardingAnswer } from '../onboarding/flow';
import type { SurveyStepConfig } from '../onboarding/survey';

/**
 * 프로필 — 닉네임·언어·초기 설정의 답을 확인하고 고치는 곳.
 *
 * 필드 목록을 여기 적지 않고 `SURVEY_STEPS` 중 **프로필 키**(`PROFILE_KEYS`)만 훑는다.
 * 측정환경·컨디션은 회차마다 묻는 답이라 프로필에 없다 — 여기 두면 고쳐도 다음 회차가
 * 다시 물어 "고쳤는데 왜 또 묻지"가 된다.
 *
 * **저장은 버튼이 한다.** 고치는 동안에는 화면 안에만 있고, 눌러야 프로필과 서버 큐에
 * 함께 남는다. 버튼은 고친 것이 있을 때만 켜지고, 되돌려 놓으면 다시 꺼진다 — 같은 값을
 * 두 번 보내지 않는다.
 *
 * 대가를 알고 고른 것이다: 안 누르고 나가면 고친 것이 사라진다. 그 대신 서버에 무엇이
 * 언제 가는지가 눈에 보인다 — 글자 하나에 요청 하나가 나가던 자동 저장을 대체한다.
 *
 * **배치**: 사진 → `내 정보`(닉네임·언어) → `내 답변`(설문). 한 줄에 항목 하나인
 * 목록 카드를 쓴다(`더보기` 탭과 같은 모양) — 라벨과 값이 같은 줄에 서면 훑을 때
 * "무엇이 무엇으로 되어 있는지"가 한눈에 들어오고, 6개 항목이 화면 하나에 들어온다.
 * 줄 전체가 `<label>`이라 어디를 눌러도 그 줄의 입력이 열린다.
 *
 * 저장 버튼은 고칠 것이 생기면 화면 바닥에 **떠 있다**. 닉네임을 고치고 저장하려면
 * 화면 끝까지 내려가야 했는데, 고치는 자리와 저장하는 자리가 멀수록 저장하지 않고
 * 나가기 쉽다. 이 화면이 탭바를 감추는 것도 같은 이유다(`fullscreenPaths.ts`) —
 * 바닥에 나가는 문과 저장 버튼이 겹쳐 서면 둘 다 좁아진다.
 */

/** 프로필 필드로 그릴 설문 단계 — 선택형만(`text`는 닉네임 입력이 따로 있다). */
const PROFILE_FIELDS = PROFILE_KEYS.flatMap((key) => {
  const step = surveyStepById(key);
  return step && step.kind !== 'text' && step.kind !== 'multi' ? [step] : [];
});

/** 저장했다는 표시가 머무는 시간. 읽고 나면 사라져야 다음 편집을 가린다. */
const SAVED_NOTICE_MS = 1800;

const cardClass =
  'divide-y divide-bk-50 overflow-hidden rounded-[20px] bg-white shadow-card';

const rowClass =
  'flex min-h-[56px] w-full items-center justify-between gap-4 px-4 py-3 text-sm text-muted-foreground';

/** 값 쪽 — 줄의 오른쪽 끝에 붙고, 길면 줄이지 않고 잘라 라벨을 밀지 않는다. */
const valueClass =
  'min-w-0 truncate appearance-none bg-transparent text-right text-base font-medium text-foreground outline-none';

const sectionClass = 'mt-8 text-sm font-semibold text-[hsl(var(--title))]';

const Row = ({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) => (
  <label htmlFor={htmlFor} className={rowClass}>
    <span className="shrink-0">{label}</span>
    {children}
  </label>
);

/**
 * 셀렉트 한 줄. 화살표는 `pointer-events-none` 이 아니라 그냥 형제다 — 줄 전체가
 * `<label>` 이라 화살표를 눌러도 셀렉트가 열린다.
 */
const SelectValue = ({
  id,
  value,
  onChange,
  children,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) => (
  <span className="flex min-w-0 flex-1 items-center justify-end gap-1">
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={valueClass}
    >
      {children}
    </select>
    <ChevronDown
      className="h-4 w-4 shrink-0 text-muted-foreground"
      aria-hidden="true"
    />
  </span>
);

/**
 * 선택형 답 하나. `year`는 값 하나를 고른다는 점에서 `single`과 같아 같은 셀렉트를 쓴다
 * (survey.ts의 검증도 그렇게 갈린다). `multi`는 셀렉트로 그리면 선택이 조용히 하나로
 * 깎이므로 이 화면이 다루지 않는다 — 지금 다중 선택 단계는 없다.
 *
 * 라벨은 설문의 질문이 아니라 `web.profile.fields.*` 다. 질문문("{{nick}}님 노래를\n
 * 얼마나 파보셨어요?")은 한 화면에 하나씩 물을 때의 문장이라, 목록에 여섯 개를 눕히면
 * 줄바꿈과 호칭이 섞여 무엇을 고르는 자리인지가 오히려 안 보인다.
 */
const SurveyField = ({
  step,
  value,
  onChange,
}: {
  step: SurveyStepConfig;
  value: OnboardingAnswer | undefined;
  onChange: (value: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Row label={t(`web.profile.fields.${step.id}`)} htmlFor={step.id}>
      <SelectValue
        id={step.id}
        value={typeof value === 'string' ? value : ''}
        onChange={onChange}
      >
        <option value="">{t('web.profile.unanswered')}</option>
        {step.options.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.labelKey, optionLabelParams(step, option))}
          </option>
        ))}
      </SelectValue>
    </Row>
  );
};

export const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const goBack = useGoBack();
  /** 저장된 값. 더러움 판정의 기준선이자, 저장이 성공하면 옮겨 오는 자리다. */
  const [saved, setSaved] = useState(() => loadProfile()?.answers ?? {});
  const [draft, setDraft] = useState(saved);
  const language = i18n.resolvedLanguage ?? i18n.language;
  const [savedLanguage, setSavedLanguage] = useState(language);
  const { hash } = useLocation();
  const [justSaved, setJustSaved] = useState(false);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (noticeTimer.current !== null) {
        clearTimeout(noticeTimer.current);
      }
    },
    [],
  );

  const update = (key: ProfileKey, value: OnboardingAnswer): void => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const dirty = useMemo(
    () =>
      language !== savedLanguage ||
      PROFILE_KEYS.some((key) => draft[key] !== saved[key]),
    [draft, saved, language, savedLanguage],
  );

  const commit = (): void => {
    // 완료 시각은 옮기지 않는다 — 수정은 초기 설정을 다시 마친 것이 아니다.
    const profile = saveProfile(draft, loadProfile()?.completedAt);
    queueProfileSync(toProfileBody(draft, language));
    setSaved(profile.answers);
    setDraft(profile.answers);
    setSavedLanguage(language);
    setJustSaved(true);
    if (noticeTimer.current !== null) {
      clearTimeout(noticeTimer.current);
    }
    noticeTimer.current = setTimeout(
      () => setJustSaved(false),
      SAVED_NOTICE_MS,
    );
  };

  /**
   * 저장 안 한 수정을 들고 나가면 조용히 사라진다 — 라우터가 `<Routes>` 컴포넌트 API라
   * `useBlocker`가 없으므로(admin과 같은 제약) 나가는 자리에서 직접 묻는다.
   * 새로고침·앱 종료는 `beforeunload`가 맡는다.
   */
  /**
   * 더보기의 `언어` 줄이 `#language` 로 보낸다 — 도착하면 그 줄로 데려가 포커스까지 준다.
   * 해시는 라우터가 스크롤해 주지 않는다(`<Routes>` 컴포넌트 API).
   */
  useEffect(() => {
    if (hash !== '#language') {
      return;
    }
    const el = document.getElementById('language');
    if (el instanceof HTMLElement) {
      el.scrollIntoView?.({ block: 'center' });
      el.focus({ preventScroll: true });
    }
  }, [hash]);

  const leave = (): void => {
    if (dirty && !window.confirm(t('web.profile.unsavedConfirm'))) {
      return;
    }
    // 언어는 고르는 순간 화면에 붙는다(미리보기). 저장 안 하고 나가면 **되돌린다** —
    // 안 그러면 저장된 프로필은 한국어인데 앱은 영어인 채로 남는다(2026-09-16).
    if (language !== savedLanguage) {
      void i18n.changeLanguage(savedLanguage);
    }
    void goBack();
  };

  useEffect(() => {
    if (!dirty) {
      return;
    }
    const onUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const answers = draft;
  const raw = answers['nick'];
  const nick = typeof raw === 'string' ? raw : '';
  /** 저장할 것이 있거나 방금 저장했을 때만 바가 떠오른다. */
  const floating = dirty || justSaved;

  return (
    <div
      className="flex flex-1 flex-col px-5 pt-[calc(var(--safe-area-top,0px)+1rem)]"
      // 탭바가 없는 화면이라 바닥 여백을 셸이 대신 넣어 주지 않는다.
      style={{ paddingBottom: BOTTOM_INSET }}
    >
      {/* 더보기 목록의 `>` 드릴다운으로 들어온 화면이라, 나가는 길도 같은 모양이어야
          한다(HistoryPage와 같은 헤더 패턴) — 탭을 다시 눌러야만 나가는 막다른 길이었다. */}
      <header className="flex items-center gap-2">
        <button
          type="button"
          onClick={leave}
          aria-label={t('web.common.back')}
          className="-ml-2 p-2"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-bold">{t('web.profile.title')}</h1>
      </header>

      <div className="mt-6">
        <ProfilePhotoField />
      </div>

      <h2 className={sectionClass}>{t('web.profile.identity')}</h2>
      <div className={cn(cardClass, 'mt-3')}>
        <Row label={t('web.onboarding.nick.label')} htmlFor="nick">
          <input
            id="nick"
            type="text"
            value={nick}
            maxLength={SURVEY_STEPS.find((s) => s.id === 'nick')?.maxLength}
            onChange={(e) => update('nick', e.target.value)}
            placeholder={t('web.onboarding.nick.placeholder')}
            className={cn(
              valueClass,
              'flex-1 placeholder:text-muted-foreground',
            )}
          />
        </Row>

        <Row label={t('web.language.label')} htmlFor="language">
          <SelectValue
            id="language"
            value={language}
            onChange={(value) => void i18n.changeLanguage(value)}
          >
            {LANGUAGES.map((lng) => (
              <option key={lng} value={lng}>
                {t(`web.language.${lng}`)}
              </option>
            ))}
          </SelectValue>
        </Row>
      </div>

      <h2 className={sectionClass}>{t('web.profile.answers')}</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        {t('web.profile.hint')}
      </p>
      <div className={cn(cardClass, 'mt-3')}>
        {PROFILE_FIELDS.map((step) => (
          <SurveyField
            key={step.id}
            step={step}
            value={answers[step.id]}
            onChange={(value) => update(step.id, value)}
          />
        ))}
      </div>

      {/* 고친 것이 있을 때만 탭바 위로 떠오른다(`sticky`). 고칠 것이 없으면 화면 끝에
          가만히 서서 자리만 지킨다 — 바뀐 것도 없는데 떠 있으면 화면만 좁아진다.
          버튼 자체는 두 상태 모두에 있다: 이 화면은 저장을 눌러야 남는 화면이고,
          그 사실이 고치기 전에도 보여야 한다. */}
      <div
        className={cn('mt-8', floating && 'sticky z-10')}
        style={floating ? { bottom: BOTTOM_INSET } : undefined}
      >
        <div
          className={cn(
            'flex items-center gap-2 rounded-2xl',
            floating && 'bg-background/90 py-2 backdrop-blur',
          )}
        >
          {justSaved && (
            <p
              aria-live="polite"
              className="flex min-w-0 items-center gap-1.5 pl-2 text-sm font-medium text-muted-foreground"
            >
              <Check
                className="h-4 w-4 shrink-0 text-primary"
                aria-hidden="true"
              />
              {t('web.profile.saved')}
            </p>
          )}
          <button
            type="button"
            onClick={commit}
            disabled={!dirty}
            // 떠 있을 때나 흐름에 있을 때나 같은 버튼이다 — 크기가 달라지면
            // 같은 버튼이 두 개인 것처럼 보인다. `flex-1` 이라 혼자면 폭을 다 쓴다.
            className="flex-1 rounded-xl bg-primary px-4 py-3.5 text-base font-semibold text-primary-foreground disabled:bg-muted disabled:text-muted-foreground"
          >
            {t('web.profile.save')}
          </button>
        </div>
      </div>
    </div>
  );
};
